(() => {
  'use strict';

  // =====================
  // Cart drawer
  // =====================
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartDrawerBody = cartDrawer && cartDrawer.querySelector('[data-cart-drawer-body]');
  const cartDrawerFooter = cartDrawer && cartDrawer.querySelector('[data-cart-drawer-footer]');
  const cartDrawerSubtotal = cartDrawer && cartDrawer.querySelector('[data-cart-drawer-subtotal]');

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function renderCart(cart) {
    if (!cartDrawerBody) return;
    if (!cart || cart.item_count === 0) {
      cartDrawerBody.innerHTML = '<div class="cart-drawer__empty">YOUR CART IS EMPTY</div>';
      if (cartDrawerFooter) cartDrawerFooter.hidden = true;
      return;
    }
    cartDrawerBody.innerHTML = cart.items.map((item) => {
      const variantBit = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<div class="cart-drawer__item-variant">${escapeHtml(item.variant_title)}</div>` : '';
      return `
        <div class="cart-drawer__item" data-key="${escapeHtml(item.key)}">
          ${item.image ? `<a href="${escapeHtml(item.url)}"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}"></a>` : '<div></div>'}
          <div class="cart-drawer__item-info">
            <a class="cart-drawer__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${variantBit}
            <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
            <div class="cart-drawer__item-controls">
              <input type="number" class="cart-drawer__item-qty" value="${item.quantity}" min="0" aria-label="Quantity" data-cart-qty data-key="${escapeHtml(item.key)}">
              <a href="#" class="cart-drawer__item-remove" data-cart-remove data-key="${escapeHtml(item.key)}">Remove</a>
            </div>
          </div>
        </div>
      `;
    }).join('');
    if (cartDrawerFooter) cartDrawerFooter.hidden = false;
    if (cartDrawerSubtotal) cartDrawerSubtotal.textContent = formatMoney(cart.total_price);
  }

  async function refreshCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      renderCart(cart);
    } catch (err) {
      // ignore — drawer stays empty if cart fetch fails
    }
  }

  function openCartDrawer() {
    if (!cartDrawer) return;
    refreshCart();
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  if (cartDrawer) {
    cartDrawer.querySelectorAll('[data-cart-drawer-close]').forEach((el) => {
      el.addEventListener('click', closeCartDrawer);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('is-open')) closeCartDrawer();
    });
    cartDrawer.addEventListener('change', async (e) => {
      if (!e.target.matches('[data-cart-qty]')) return;
      const key = e.target.dataset.key;
      const qty = Math.max(0, parseInt(e.target.value, 10) || 0);
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
      } finally {
        refreshCart();
      }
    });
    cartDrawer.addEventListener('click', async (e) => {
      if (!e.target.matches('[data-cart-remove]')) return;
      e.preventDefault();
      const key = e.target.dataset.key;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      } finally {
        refreshCart();
      }
    });
  }

  // Intercept cart icon clicks → open drawer instead of navigating to /cart
  document.querySelectorAll('.site-cart').forEach((cart) => {
    cart.addEventListener('click', (e) => {
      e.preventDefault();
      openCartDrawer();
    });
  });

  // =====================
  // Product form: variant picker, thumbnail crossfade, async add-to-cart
  // =====================
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const jsonNode = form.querySelector('[data-product-json]');
    if (!jsonNode) return;

    let variants;
    try { variants = JSON.parse(jsonNode.textContent); } catch { return; }

    const selects = form.querySelectorAll('[data-option-index]');
    const variantInput = form.querySelector('[data-variant-id]');
    const priceEl = form.querySelector('[data-price]');
    const addBtn = form.querySelector('.product-card-full__add');
    const addLabel = form.querySelector('[data-add-label]');

    function chosenOptions() {
      return Array.from(selects).map((s) => s.value);
    }

    function findVariant() {
      const opts = chosenOptions();
      if (opts.some((v) => !v)) return null;
      return variants.find((v) =>
        (v.options || []).every((opt, i) => opt === opts[i])
      );
    }

    function update() {
      const v = findVariant();
      if (!v) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'select options';
        return;
      }
      if (variantInput) variantInput.value = v.id;
      if (priceEl) priceEl.textContent = formatMoney(v.price);
      if (addBtn) addBtn.disabled = !v.available;
      if (addLabel) addLabel.textContent = v.available ? 'add to cart' : 'sold out';
    }

    selects.forEach((s) => s.addEventListener('change', update));

    // Thumbnail swap with crossfade
    const mainImage = form.parentElement.querySelector('[data-main-image]');
    form.parentElement.querySelectorAll('[data-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!mainImage) return;
        const src = btn.dataset.src;
        const srcset = btn.dataset.srcset;
        const zoomSrc = btn.dataset.zoomSrc;
        mainImage.classList.add('is-swapping');
        const preload = new Image();
        const swap = () => {
          if (src) mainImage.src = src;
          if (srcset) mainImage.srcset = srcset;
          if (zoomSrc) mainImage.dataset.zoomSrc = zoomSrc;
          requestAnimationFrame(() => mainImage.classList.remove('is-swapping'));
        };
        preload.onload = swap;
        preload.onerror = swap;
        preload.src = src || '';
      });
    });

    // Async add-to-cart → open drawer on success
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!addBtn) return;
      addBtn.disabled = true;
      const original = addLabel ? addLabel.textContent : '';
      if (addLabel) addLabel.textContent = 'adding...';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(new FormData(form)).toString(),
        });
        if (!res.ok) throw new Error('add failed');
        if (addLabel) addLabel.textContent = 'added ✓';
        setTimeout(() => {
          if (addLabel) addLabel.textContent = original || 'add to cart';
          addBtn.disabled = false;
        }, 1400);
        openCartDrawer();
      } catch (err) {
        if (addLabel) addLabel.textContent = 'error — try again';
        addBtn.disabled = false;
      }
    });
  });

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // =====================
  // Logo as nav toggle
  // =====================
  const navToggle = document.querySelector('[data-nav-toggle]');
  const siteHeader = document.querySelector('.site-header');
  if (navToggle && siteHeader) {
    navToggle.addEventListener('click', (e) => {
      e.preventDefault();
      const collapsed = siteHeader.classList.toggle('is-nav-collapsed');
      navToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
  }

  // =====================
  // Lightbox: tap main image to zoom
  // =====================
  const lightbox = document.querySelector('[data-lightbox]');
  if (lightbox && typeof lightbox.showModal === 'function') {
    const lightboxImage = lightbox.querySelector('[data-lightbox-image]');
    const closeBtn = lightbox.querySelector('[data-lightbox-close]');

    document.querySelectorAll('[data-zoom-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        const img = trigger.querySelector('[data-main-image]');
        if (!img) return;
        e.preventDefault();
        lightboxImage.src = img.dataset.zoomSrc || img.currentSrc || img.src;
        lightboxImage.alt = img.alt || '';
        lightbox.showModal();
      });
    });

    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox || e.target === lightboxImage) lightbox.close();
    });
    if (closeBtn) closeBtn.addEventListener('click', () => lightbox.close());
    lightbox.addEventListener('close', () => { lightboxImage.src = ''; });
  }
})();
